import { type ReactNode, useState } from 'react'
import { Head, router, useForm, usePage } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TagCombobox } from '@/components/TagCombobox'
import { Archive, ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react'
import type { ResearchCustomFieldDef } from '@/types/research'

type Props = {
  custom_fields: ResearchCustomFieldDef[]
  archived_fields: ResearchCustomFieldDef[]
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'tag', label: 'Tag' },
  { value: 'url', label: 'URL' },
]

export default function ResearchCustomFieldsIndex({ custom_fields, archived_fields }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ResearchCustomFieldDef | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ResearchCustomFieldDef | null>(null)
  const pageErrors = usePage().props.errors as Record<string, string | string[]> | undefined

  const { data, setData, post, patch, processing, reset } = useForm({
    research_custom_field: {
      label: '',
      field_type: 'text',
      options: [] as string[],
      default_value: '',
      help_text: '',
      required: false,
    },
  })

  const field = data.research_custom_field

  function setField<K extends keyof typeof field>(key: K, value: (typeof field)[K]) {
    setData('research_custom_field', { ...field, [key]: value })
  }

  function fieldError(key: string): string | undefined {
    const err = pageErrors?.[key]
    return Array.isArray(err) ? err[0] : err
  }

  function openNew() {
    reset()
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(target: ResearchCustomFieldDef) {
    setData('research_custom_field', {
      label: target.label,
      field_type: target.field_type,
      options: target.options,
      default_value: target.default_value ?? '',
      help_text: target.help_text ?? '',
      required: target.required,
    })
    setEditing(target)
    setDialogOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const options = { onSuccess: () => setDialogOpen(false) }
    if (editing) {
      patch(`/admin/research/custom_fields/${editing.id}`, options)
    } else {
      post('/admin/research/custom_fields', options)
    }
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= custom_fields.length) return
    const ids = custom_fields.map(f => f.id)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    router.patch('/admin/research/custom_fields/reorder', { ids }, { preserveState: false })
  }

  function confirmDelete() {
    if (!deleteTarget) return
    router.delete(`/admin/research/custom_fields/${deleteTarget.id}`)
    setDeleteTarget(null)
  }

  return (
    <>
      <Head title="Research - Field Config" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            Custom fields appear on every research event form, after the built-in fields.
          </p>
          <Button onClick={openNew}>
            <Plus className="mr-2 size-4" />
            Add Field
          </Button>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Order</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Key</TableHead>
                <TableHead className="text-center">Required</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {custom_fields.map((customField, index) => (
                <TableRow key={customField.id}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        title="Move up"
                      >
                        <ChevronUp className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        disabled={index === custom_fields.length - 1}
                        onClick={() => move(index, 1)}
                        title="Move down"
                      >
                        <ChevronDown className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{customField.label}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{customField.field_type}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {customField.key}
                  </TableCell>
                  <TableCell className="text-center">
                    {customField.required ? 'Yes' : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="size-8" onClick={() => openEdit(customField)} title="Edit">
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-muted-foreground"
                        onClick={() =>
                          router.patch(`/admin/research/custom_fields/${customField.id}/archive`)
                        }
                        title="Archive"
                      >
                        <Archive className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {custom_fields.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    No custom fields yet. Add one to extend the event form.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {archived_fields.length > 0 && (
          <>
            <h2 className="mt-8 mb-3 text-lg font-semibold tracking-tight">Archived fields</h2>
            <p className="mb-3 text-sm text-muted-foreground">
              Archived fields are hidden from the event form; saved values are kept. Restore a
              field to bring it back, or delete it permanently.
            </p>
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archived_fields.map(archivedField => (
                    <TableRow key={archivedField.id}>
                      <TableCell className="font-medium">{archivedField.label}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{archivedField.field_type}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {archivedField.key}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              router.patch(
                                `/admin/research/custom_fields/${archivedField.id}/restore`,
                              )
                            }
                          >
                            Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteTarget(archivedField)}
                            title="Delete permanently"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit field' : 'Add field'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'The system key stays fixed so existing data keeps working.'
                : 'The system key is generated automatically from the label.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="field-label">
                Field Label<span className="text-destructive"> *</span>
              </Label>
              <Input
                id="field-label"
                placeholder="e.g. Catering Company"
                value={field.label}
                onChange={e => setField('label', e.target.value)}
              />
              {fieldError('label') && (
                <p className="text-xs text-destructive">{fieldError('label')}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="field-type">Field Type</Label>
                <Select
                  value={field.field_type}
                  onValueChange={value => setField('field_type', value)}
                >
                  <SelectTrigger id="field-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {FIELD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-key">System Key</Label>
                <Input
                  id="field-key"
                  value={editing?.key ?? ''}
                  placeholder="auto-generated"
                  disabled
                />
              </div>
            </div>

            {(field.field_type === 'select' || field.field_type === 'tag') && (
              <TagCombobox
                label="Options"
                placeholder="Type an option and press Enter..."
                suggestions={[]}
                selected={field.options}
                onChange={items => setField('options', items)}
                allowCustom
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="field-default">Default Value</Label>
                <Input
                  id="field-default"
                  placeholder="Pre-filled value"
                  value={field.default_value}
                  onChange={e => setField('default_value', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-help">Help Text</Label>
                <Input
                  id="field-help"
                  placeholder="Instructions for the user..."
                  value={field.help_text}
                  onChange={e => setField('help_text', e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="field-required"
                checked={field.required}
                onCheckedChange={checked => setField('required', checked)}
              />
              <Label htmlFor="field-required">Required field</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {editing ? 'Save changes' : 'Add Field'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently delete field</DialogTitle>
            <DialogDescription>
              Permanently delete &ldquo;{deleteTarget?.label}&rdquo;? Values already saved on
              events stay in the data but will no longer be shown anywhere. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

ResearchCustomFieldsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
